// @ts-check
import {
  Box,
  Link,
  Table,
  Title,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '~/design-system';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import React from 'react';
import getBottlebooksFieldsMap from '../MapFields/getBottlebooksFieldsMap';
import FieldErrorCell from './FieldErrorCell';

/**
 * Renders the validation bottom table with detailed errors for the given column
 * @param {Object} props
 * @param {Object} props.sourceFields
 * @param {boolean} props.canAllowCreation
 * @param {Object} props.bottlebooksFields
 * @param {Object} props.column
 * @param {Object} props.dataRowIndex
 * @param {Object} props.masterListColumns
 */
export default function FieldsTableErrors({
  canAllowCreation,
  sourceFields,
  bottlebooksFields,
  column,
  dataRowIndex,
  masterListColumns,
  ...rest
}) {
  const { i18n } = useLingui();
  const [showAll, setShowAll] = React.useState(false);
  const errorsTableRef = React.useRef(null);
  const columnLabel = i18n._(column.label);
  React.useEffect(() => {
    window.scrollTo({
      behavior: 'smooth',
      top: errorsTableRef?.current?.offsetTop,
    });
  }, [errorsTableRef]);
  const bottlebooksFieldsMap = getBottlebooksFieldsMap({
    bottlebooksFields,
    sourceFields,
    fieldsToImport: masterListColumns,
  });
  const errors = bottlebooksFieldsMap?.[column.key]?.errors;
  const hasErrors = errors?.length > 0;
  if (!hasErrors) return null;

  return (
    <Box {...rest} ref={errorsTableRef}>
      <Title variant="small" sx={{ textAlign: 'center', marginBottom: 3 }}>
        {canAllowCreation ? (
          <Trans>Rows with products to be created</Trans>
        ) : (
          <Trans>Errors</Trans>
        )}
      </Title>
      <Table
        sx={{
          border: '1px solid',
          borderColor: 'brand.lightGray',
          width: '100%',
        }}
      >
        <TableHead>
          <TableRow>
            <TableHeader
              sx={{
                width: 90,
                backgroundColor: 'brand.lightGray',
                textAlign: 'center',
              }}
            >
              <Text variant="smallest">
                <Trans>Row</Trans>
              </Text>
            </TableHeader>
            <TableHeader
              sx={{ backgroundColor: 'brand.lightGray', textAlign: 'left' }}
            >
              <Text variant="smallest">
                {columnLabel} /{' '}
                {bottlebooksFieldsMap[column.key]?.importFieldName}
              </Text>
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {errors?.map((error, j) => {
            if (showAll) {
              return (
                <TableRow key={j}>
                  <TableCell
                    sx={{ width: 90, height: '65px', textAlign: 'center' }}
                  >
                    <Text variant="smallest">{error.index + 2}</Text>
                  </TableCell>
                  <TableCell sx={{ height: '65px', textAlign: 'left' }}>
                    <FieldErrorCell
                      error={error}
                      bottlebooksFieldsMap={bottlebooksFieldsMap}
                      dataRowIndex={dataRowIndex}
                      columnLabel={columnLabel}
                    />
                  </TableCell>
                </TableRow>
              );
            }
            return j < 6 ? (
              <TableRow key={j}>
                <TableCell
                  sx={{ width: 90, height: '65px', textAlign: 'center' }}
                >
                  <Text variant="smallest">{error.index + 2}</Text>
                </TableCell>
                <TableCell sx={{ height: '65px', textAlign: 'left' }}>
                  <FieldErrorCell
                    error={error}
                    bottlebooksFieldsMap={bottlebooksFieldsMap}
                    dataRowIndex={dataRowIndex}
                    columnLabel={columnLabel}
                  />
                </TableCell>
              </TableRow>
            ) : j === 6 ? (
              <TableRow key={j}>
                <TableCell></TableCell>
                <TableCell sx={{ height: '65px', textAlign: 'left' }}>
                  <Text>
                    <Link onClick={() => setShowAll(true)}>Show more...</Link>
                  </Text>
                </TableCell>
              </TableRow>
            ) : null;
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
